import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";

const ChildrenEditor = ({ value = [], onChange }) => {
  const set = (i, k, v) => onChange(value.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  return (
    <div className="space-y-2">
      {value.map((c, i) => (
        <div key={i} className="grid grid-cols-[1fr_9rem_5rem_auto] gap-2 items-center">
          <Input placeholder="Prénom" value={c.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`child-name-${i}`} />
          <Input type="date" value={c.birth_date} onChange={(e) => set(i, "birth_date", e.target.value)} data-testid={`child-birth-${i}`} />
          <Select value={c.sex} onValueChange={(v) => set(i, "sex", v)}>
            <SelectTrigger data-testid={`child-sex-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="F">F</SelectItem><SelectItem value="M">M</SelectItem></SelectContent>
          </Select>
          <button type="button" className="p-2 text-stone-400 hover:text-red-700" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`child-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { name: "", birth_date: "", sex: "F" }])} data-testid="child-add-button">
        <Plus className="h-4 w-4 mr-1" /> Ajouter un enfant
      </Button>
    </div>
  );
};

const MultiRelation = ({ id, value = [], options = [], onChange }) => (
  <div className="rounded-md border border-stone-200 max-h-40 overflow-y-auto divide-y divide-stone-100" data-testid={id}>
    {options.length === 0 && <p className="p-2 text-sm text-stone-500">Aucun choix disponible</p>}
    {options.map((o) => (
      <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-stone-50">
        <input type="checkbox" checked={value.includes(o.value)} onChange={() => onChange(value.includes(o.value) ? value.filter((v) => v !== o.value) : [...value, o.value])} data-testid={`${id}-${o.value}`} />
        {o.label}
      </label>
    ))}
  </div>
);

export const FormField = ({ field, value, onChange, options }) => {
  const id = `field-${field.key}`;
  let control;
  if (field.type === "textarea") control = <Textarea id={id} rows={5} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={id} />;
  else if (field.type === "relations") control = <MultiRelation id={id} value={value || []} options={options} onChange={onChange} />;
  else if (field.type === "medlist") control = <Input id={id} value={(value || []).map((m) => m.name || m).join(", ")} placeholder="Ex. Amoxicilline, Paracétamol" onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean).map((name) => ({ name })))} data-testid={id} />;
  else if (field.type === "children") control = <ChildrenEditor value={value} onChange={onChange} />;
  else if (field.type === "list") control = <Input id={id} value={(value || []).join(", ")} placeholder="Séparer par des virgules" onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid={id} />;
  else if (field.type === "bool") control = (
    <Select value={value == null ? undefined : String(value)} onValueChange={(v) => onChange(v === "true")}>
      <SelectTrigger id={id} data-testid={id}><SelectValue placeholder="Choisir…" /></SelectTrigger>
      <SelectContent><SelectItem value="true">Oui</SelectItem><SelectItem value="false">Non</SelectItem></SelectContent>
    </Select>
  );
  else if (field.type === "number") control = <Input id={id} type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} required={field.required} data-testid={id} />;
  else if (field.type === "select" || field.type === "relation") {
    const opts = field.type === "relation" ? options || [] : field.options;
    control = (
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={id}><SelectValue placeholder="Choisir…" /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  } else control = <Input id={id} type={field.type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={field.required} data-testid={id} />;
  return (
    <div className={`space-y-1.5 ${field.full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.12em] text-stone-500">{field.label}{field.required && " *"}</Label>
      {control}
    </div>
  );
};
